import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCard, SpecraCardContent, SpecraFileRow, SpecraHeading, SpecraInlineBanner, SpecraTag, SpecraText } from '@mono/ui-components';
import { Link } from 'react-router-dom';
import { useProjectFiles } from '../../../../hooks/useProjectFiles';
import { useSolicitationInsights } from '../../../../hooks/useSolicitationInsights';
import { LifecycleNodeKind, useLinkedNodes } from '../../../LinkedChips/useLinkedNodes';
import { formatDate, hasAnyInsights, isAmendment, isUnclassified, isUnclassifiedUpload, kindLabel, selectBasisDocument, selectExtractionState, selectNewerUnreviewed, selectOrderedPackage, selectSolicitationPackage } from './solicitationOfRecord';
export function SolicitationOfRecordPanel({ projectId }) {
    const filesQuery = useProjectFiles(projectId);
    const insightsQuery = useSolicitationInsights(projectId);
    const linksQuery = useLinkedNodes(LifecycleNodeKind.PROPOSAL, projectId);
    if (filesQuery.isLoading) {
        return /*#__PURE__*/ _jsx(SpecraCard, {
            className: "mb-4",
            children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    as: "div",
                    size: "caption",
                    tone: "muted",
                    children: "Checking the solicitation of record…"
                })
            })
        });
    }
    if (filesQuery.isError || !filesQuery.data) {
        return /*#__PURE__*/ _jsx(SpecraCard, {
            className: "mb-4",
            children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    as: "div",
                    size: "caption",
                    tone: "muted",
                    children: "We could not load this proposal's sources."
                })
            })
        });
    }
    const files = filesQuery.data.files;
    const pkg = selectSolicitationPackage(files);
    const basis = selectBasisDocument(files);
    const ordered = selectOrderedPackage(files);
    const newer = selectNewerUnreviewed(files, basis);
    const newestUnreviewed = newer.length > 0 ? [
        ...newer
    ].sort((a, b)=>new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())[0] : undefined;
    const pipeline = insightsQuery.data?.pipeline;
    const extraction = selectExtractionState(pkg, hasAnyInsights(pipeline));
    const otherFilesCount = files.length - pkg.length;
    const pursuitEdge = !linksQuery.isLoading ? (linksQuery.data?.edges ?? []).find((e)=>e.node?.kind === LifecycleNodeKind.PURSUIT) : undefined;
    const showProvenance = !linksQuery.isLoading && !linksQuery.isError && !!linksQuery.data;
    return /*#__PURE__*/ _jsx(SpecraCard, {
        className: "mb-4",
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    size: "sm",
                    children: "Solicitation of record"
                }),
                /*#__PURE__*/ _jsx(BasisLine, {
                    basis: basis,
                    pkgEmpty: pkg.length === 0
                }),
                showProvenance && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "div",
                    size: "caption",
                    tone: "muted",
                    children: pursuitEdge ? `From capture: ${pursuitEdge.node?.label?.trim() || 'Pursuit'}` : 'Manually uploaded to this proposal. No capture history.'
                }),
                pkg.length > 0 && /*#__PURE__*/ _jsx("div", {
                    className: "space-y-1.5",
                    children: ordered.map((file)=>/*#__PURE__*/ _jsx(SpecraFileRow, {
                            name: file.name,
                            category: `${kindLabel(file)}${file.amendmentNumber ? ` ${file.amendmentNumber}` : ''}`,
                            meta: rowMeta(file),
                            actions: isUnclassified(file) ? /*#__PURE__*/ _jsx(UnclassifiedChip, {}) : undefined
                        }, file.attachmentId))
                }),
                otherFilesCount > 0 && /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "div",
                    size: "caption",
                    tone: "muted",
                    children: [
                        otherFilesCount,
                        " working / reference file",
                        otherFilesCount === 1 ? '' : 's',
                        " below"
                    ]
                }),
                newestUnreviewed && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    children: newerSourceMessage(newestUnreviewed)
                }),
                extraction && /*#__PURE__*/ _jsx(ExtractionNotice, {
                    state: extraction,
                    projectId: projectId
                })
            ]
        })
    });
}
function BasisLine({ basis, pkgEmpty }) {
    if (pkgEmpty) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "div",
                    className: "font-medium",
                    size: "body-ui",
                    children: "No solicitation of record yet"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "div",
                    size: "caption",
                    tone: "muted",
                    children: "Upload the solicitation your prime provided using the uploader below."
                })
            ]
        });
    }
    if (!basis) return null;
    const date = formatDate(basis.uploadedAt);
    const who = basis.uploadedBy?.name;
    const tail = uploadTail(who, date);
    if (isUnclassifiedUpload(basis)) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            as: "div",
            size: "body-ui",
            children: `Working from "${basis.name}"${tail ? ` — ${tail}` : ''}. Not yet classified: nothing has confirmed this is the solicitation.`
        });
    }
    const label = isAmendment(basis) ? `Amendment ${basis.amendmentNumber ?? ''}`.trim() : 'the base solicitation';
    return /*#__PURE__*/ _jsx(SpecraText, {
        as: "div",
        size: "body-ui",
        children: `Working from ${label} — "${basis.name}"${tail ? `, ${tail}` : ''}`
    });
}
/** `uploaded by <who> on <date>`, degrading cleanly when either half is missing. */ function uploadTail(who, date) {
    if (who && date) return `uploaded by ${who} on ${date}`;
    if (who) return `uploaded by ${who}`;
    if (date) return `uploaded ${date}`;
    return '';
}
function rowMeta(file) {
    const date = formatDate(file.uploadedAt);
    const who = file.uploadedBy?.name;
    return [
        who,
        date
    ].filter(Boolean).join(' · ');
}
function newerSourceMessage(file) {
    const date = formatDate(file.uploadedAt);
    const lead = date ? `Newer source uploaded ${date}` : 'A newer source was uploaded';
    return `${lead} — not yet reviewed against your current work. "${file.name}"`;
}
function UnclassifiedChip() {
    const explanation = 'The pipeline did not identify this document. It can still be used.';
    return /*#__PURE__*/ _jsx(SpecraTag, {
        tone: "neutral",
        title: explanation,
        "aria-label": `Not classified. ${explanation}`,
        children: "Not classified"
    });
}
function extractionMessage(state) {
    switch(state.kind){
        case 'error':
            return `Processing failed for ${state.count} source document${state.count === 1 ? '' : 's'}. Nothing was extracted from them.`;
        case 'processing':
            return 'Reading the solicitation now. Requirements will appear here when it finishes.';
        case 'none-run':
            return 'No automated extraction has run on this source yet.';
        case 'empty':
            return 'Nothing was extracted from this source.';
        case 'ok':
            return undefined;
    }
}
function ExtractionNotice({ state, projectId }) {
    const message = extractionMessage(state);
    if (!message) return null;
    return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: state.kind === 'error' ? 'danger' : 'neutral',
        children: [
            message,
            " That is not the same as there being no requirements — add them by hand in Compliance.",
            ' ',
            /*#__PURE__*/ _jsx(Link, {
                className: "underline",
                to: `/proposal/${projectId}/v2/compliance`,
                children: "Open Compliance"
            })
        ]
    });
}
